import { Flex, Grid, GridItem, Heading, Button } from '@chakra-ui/react';
import RecommendationsCard from './RecommendationsCard';
import DemoSiteModal from './DemoSiteModal';
import InviteTeamModal from './InviteTeamModal';
import XmCloudTrialModal from './XmCloudTrialModal';
import { getCtaCallback } from 'features/domains/portal/content/utils';
import { useHistory } from 'react-router';
import {
  DEMO_SOLUTION_CARD_ID,
  getDeployApiClickClickLaunchBaseUrl,
  XmCloudTrialCardId,
  transformCardData,
  baseXMCloudUrl,
  GoLivePlanning,
} from 'features/recommendations/utils';
import {
  Link,
  Maybe,
  useGetRecommendationCardsQuery,
  useCloseRecommendationCardsMutation,
  GetRecommendationCardsDocument,
  RecommendationCard,
  useGetTenantsQuery,
  useGetGoLiveDateQuery,
} from 'gql/graphql';
import { useMainStateDispatch } from 'features/main/context';
import { openDrawer } from 'features/main/context/drawer/actionCreators';
import { useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useQueryClient } from 'react-query';
import { TokenCustomClaimKeysEnum } from '@sitecore-ui/portal-singular';
import { env } from 'features/common/config/envConfig';
import { useGetTenantsByOrgAndProductCode } from 'features/common/services/inventory/tenants';
import { useFeature } from 'lib/featureFlags/features';

const Recommendations = () => {
  const history = useHistory();
  const [, dispatch] = useMainStateDispatch();
  const { data, refetch } = useGetRecommendationCardsQuery();
  const [closeRecommendationCards] = useCloseRecommendationCardsMutation();
  const [uiCards, setUiCards] = useState<RecommendationCard[]>([]);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [showXMCloudTrialModal, setShowXMCloudTrialModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [isCreatingDemo, setIsCreatingDemo] = useState(false);
  const [platformTenantId, setPlatformTenantId] = useState<
    string | undefined
  >();
  const { getAccessTokenSilently, user } = useAuth0();
  const organizationId = user?.[TokenCustomClaimKeysEnum.ORG_ID];
  const queryClient = useQueryClient();
  const goLivePlanningEnabled = useFeature('GO_LIVE_PLANNING');

  const { data: goLiveDateData, error: goLiveError } = useGetGoLiveDateQuery({
    skip: !goLivePlanningEnabled,
  });

  const goLiveDate = goLiveDateData?.serviceNow?.goLiveDate;
  const showGoLiveDate = !goLiveDate && !goLiveError;

  const { data: activeXmCloudTenants } = useGetTenantsByOrgAndProductCode({
    organizationId,
    productCode: 'XMCloud',
    state: 'Active',
  });

  const { data: tenantsQueryData } = useGetTenantsQuery({
    variables: {
      first: 20, // Assuming trial orgs have very few tenants, increase if needed
      env,
    },
    nextFetchPolicy: 'cache-only',
  });

  // Filter for trial tenants and extract the first trial tenant ID
  const trialTenantId = useMemo(() => {
    return activeXmCloudTenants?.data?.find(
      (tenant: any) => tenant.labels?.TrialEnvironment === 'true',
    )?.id;
  }, [activeXmCloudTenants?.data]);

  // Get XM Cloud link specifically from trial tenant
  const xmCloudLink = useMemo(() => {
    const nodes = tenantsQueryData?.user?.applications?.nodes ?? [];

    return trialTenantId
      ? nodes
          ?.find((node) => node.id === trialTenantId)
          ?.appearance?.web?.actions?.nodes?.find(
            (actionNode) => actionNode.name === 'SitecoreAI',
          )?.link?.to
      : undefined;
  }, [tenantsQueryData?.user?.applications?.nodes, trialTenantId]);

  const recommendationCards = useMemo(() => {
    const allCards = data?.personalizedContent?.recommendationCards ?? [];
    const filtered = allCards.filter((card) => {
      if (card.cardId === 'GoLivePlanning') {
        return showGoLiveDate && goLivePlanningEnabled;
      }
      return true;
    });
    return transformCardData(filtered.slice(0, 3));
  }, [data, goLivePlanningEnabled, showGoLiveDate]);

  useEffect(() => setUiCards(recommendationCards), [recommendationCards]);

  const handleRecommendationClick = (card: RecommendationCard) => {
    const cardHandlers = new Map([
      [DEMO_SOLUTION_CARD_ID, () => setShowDemoModal(true)],
      [XmCloudTrialCardId, () => setShowXMCloudTrialModal(true)],
      [GoLivePlanning, () => dispatch(openDrawer('go-live-date-drawer'))],
    ]);

    const handler = cardHandlers.get(card.cardId);
    if (handler) {
      handler();
    } else {
      onCardClickHandler(card.link);
    }
  };

  const handleCreateStart = () => {
    setIsCreatingDemo(true);
    setShowDemoModal(false);
    setShowInviteModal(true);
  };

  const handleCreateDemo = async () => {
    try {
      const token = await getAccessTokenSilently({
        organization_id: organizationId,
      });
      const deployBase = getDeployApiClickClickLaunchBaseUrl();
      const url = `${deployBase}/api/organizations/v1/demo-solution`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ create: true }),
      });
      const result = await response.json();
      const newTenantId =
        result.platformTenantId ?? result.applicationId ?? result.id;
      setPlatformTenantId(newTenantId);
      queryClient.invalidateQueries([
        'tenants-by-org-and-productcode',
        organizationId,
      ]);
      refetch();
    } catch (err) {
      console.error('Demo site creation failed', err);
    } finally {
      setIsCreatingDemo(false);
    }
  };

  const clearAllHandler = () => {
    const cardIds = uiCards.map(({ cardId }) => cardId);
    setUiCards([]);
    if (cardIds.length > 0) {
      closeRecommendationCards({
        variables: {
          cardIds,
        },
        refetchQueries: [GetRecommendationCardsDocument],
      });
    }
  };

  const onCardClickHandler = (link: Maybe<Link> | undefined) => {
    if (!link) return;

    return getCtaCallback({ link, history, dispatch: dispatch })();
  };

  const onCloseClickHandler = (id: string) => {
    const filteredCards = uiCards.filter(({ cardId }) => cardId !== id);
    setUiCards(filteredCards);
    closeRecommendationCards({
      variables: {
        cardIds: [id],
      },
      refetchQueries: [GetRecommendationCardsDocument],
    });
  };

  if (uiCards.length === 0) return null;

  return (
    <Flex
      data-testid='recommendations'
      flexDirection='column'
      width='full'
      gap={5}
    >
      <Flex width='full' justifyContent='space-between'>
        <Heading size='md' noOfLines={1}>
          Recommended for you
        </Heading>
        <Button variant='link' onClick={clearAllHandler}>
          Clear all
        </Button>
      </Flex>
      <Grid
        width='full'
        templateColumns={{ base: 'repeat(1, 1fr)', lg: 'repeat(3, 1fr)' }}
        gap={4}
      >
        {uiCards.map((c) => (
          <GridItem w='full' h='full' key={c.cardId}>
            <RecommendationsCard
              cardId={c.cardId}
              icon={c.icon}
              title={c.title}
              description={c.description}
              onClick={() => handleRecommendationClick(c)}
              onClose={() => onCloseClickHandler(c.cardId)}
            />
          </GridItem>
        ))}
      </Grid>
      <DemoSiteModal
        isOpen={showDemoModal}
        onClose={() => setShowDemoModal(false)}
        isCreating={isCreatingDemo}
        onCreate={() => {
          handleCreateStart();
          handleCreateDemo();
        }}
      />
      {showXMCloudTrialModal && (
        <XmCloudTrialModal
          isOpen={true}
          onClose={() => setShowXMCloudTrialModal(false)}
          onPrimaryButtonClick={() => {
            setShowXMCloudTrialModal(false);
            onCardClickHandler({
              type: 'external-link',
              // If xmCloudLink is not constructed yet, use link without tenantName specified
              to:
                xmCloudLink ||
                `${
                  baseXMCloudUrl[window.location.hostname]
                }/?organization=${organizationId}`,
            });
          }}
        />
      )}
      <InviteTeamModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onSend={() => setShowInviteModal(false)}
        isGiveAccessDisabled={isCreatingDemo || !platformTenantId}
        platformTenantId={platformTenantId}
      />
    </Flex>
  );
};

export default Recommendations;
